import { AppFooter } from "@bcc/components/app-footer";
import { AppHeader } from "@bcc/components/app-header";
import { AppSidebar } from "@bcc/components/app-sidebar";
import { LoadingIcon } from "@bcc/components/ui/loading-icon/loading-icon";
import { useRBAC } from "@bcc/contexts/rbac-context";
import { useSFDCAuthState } from "@client-shared/providers/auth";
import { useRequireInstallation } from "@client-shared/providers/installations";
import { useVendorContext } from "@client-shared/providers/vendor";
import type React from "react";
import { Navigate } from "react-router-dom";
import { ROUTE_PATHS } from "../lib/constants";
import { setAppLayoutTestId } from "./app-layout.utils";

interface AppLayoutProps {
  readonly children: React.ReactNode;
  readonly testId?: string;
}

export function AppLayout({ children, testId }: AppLayoutProps) {
  const { featureName } = useRBAC();
  const isShipstationRoaster = featureName === "SS";
  const { isLoading, shouldRedirectToOnboarding, shouldRedirectToHome } = useRequireInstallation();
  const [{ isRoasterAdmin }] = useSFDCAuthState();
  const [{ currentVendor }] = useVendorContext();

  if (isRoasterAdmin && !currentVendor) {
    return <Navigate to={ROUTE_PATHS.ADMIN_ROASTERS} replace />;
  }

  if (isLoading) {
    return (
      <div data-testid={setAppLayoutTestId("loading")} className="flex min-h-screen items-center justify-center bg-background">
        <LoadingIcon />
      </div>
    );
  }

  if (shouldRedirectToOnboarding && !isShipstationRoaster) {
    return <Navigate to={ROUTE_PATHS.ONBOARDING} replace />;
  }

  if (shouldRedirectToHome) {
    return <Navigate to={ROUTE_PATHS.HOME} replace />;
  }

  return (
    <div data-testid={testId ?? setAppLayoutTestId("container")} className="relative min-h-screen">
      <AppHeader />
      <div className="flex">
        <div className="print:hidden">
          <AppSidebar />
        </div>
        <main data-testid={setAppLayoutTestId("main-content")} className="ml-[264px] flex-1 min-w-0 overflow-x-hidden print:ml-0">
          <div className="min-h-[calc(100vh-72px-48px)] p-6 print:min-h-0 print:p-0">{children}</div>
          <div className="print:hidden">
            <AppFooter />
          </div>
        </main>
      </div>
    </div>
  );
}
